// Commit-time delivery for database change events.
//
// SQLite's update hook (and the web/turso manual emitters) fire one event per
// mutating STATEMENT, mid-transaction — before the write is committed. Every
// `await`ed statement is its own native round-trip, so a multi-statement
// transaction spreads its events across several microtask windows. Downstream
// invalidation (see `lib/database/change/table-change-version.ts`) coalesces
// matched events per microtask window, which meant one transaction produced
// several invalidation waves: subscribers refetched against half-written
// mid-transaction state, were re-invalidated by the next statement, and the
// wasted refetches congested the serialized SQLite queue the transaction itself
// runs on.
//
// The fix: while a transaction is open (`runWithBufferedDatabaseChanges`),
// deliveries are buffered; when the outermost transaction settles, the whole
// buffer flushes in ONE synchronous loop — a single microtask window — so
// subscribers observe the transaction's writes once, post-commit.
//
// A rolled-back transaction still flushes its buffered events: rollbacks are
// rare error paths, a spurious invalidation just refetches unchanged data, and
// always-flushing keeps overlapping transactions (tracked by a depth counter)
// sound — discarding on error could drop a concurrent transaction's committed
// events.

import { isNonEmpty } from "@-/com.npmjs.ts-array-length";

type DatabaseChangeEvent = {
  databaseFilePath: string;
  databaseName: string;
  rowId: number;
  tableName: string;
};

type DatabaseChangeListener = (event: DatabaseChangeEvent) => void;

const bufferState: {
  bufferedDeliveries: { event: DatabaseChangeEvent; listener: DatabaseChangeListener }[];
  openTransactionCount: number;
} = {
  bufferedDeliveries: [],
  openTransactionCount: 0,
};

function invokeListener(listener: DatabaseChangeListener, event: DatabaseChangeEvent): void {
  try {
    listener(event);
  } catch {
    // Listeners must not break the write path; swallow to match
    // `emitManualDatabaseChange`'s fire-and-forget semantics.
  }
}

/**
 * Deliver `event` to `listener` now — unless a buffered transaction is open, in
 * which case the delivery is queued and replayed (in order) when the outermost
 * transaction settles. The change-listener shims route every subscription
 * through this, so both the native update-hook feed and the manual emitter feed
 * observe transactions atomically.
 */
export function deliverDatabaseChange(
  listener: DatabaseChangeListener,
  event: DatabaseChangeEvent,
): void {
  if (bufferState.openTransactionCount > 0) {
    bufferState.bufferedDeliveries.push({ event, listener });
    return;
  }
  invokeListener(listener, event);
}

/**
 * Run `run` (a database transaction) with change-event delivery buffered until
 * it settles. Wrap the WHOLE driver `transaction(...)` call — it resolves only
 * after COMMIT/ROLLBACK, so the flush in `finally` is guaranteed post-commit.
 * Reentrant: overlapping/nested calls flush once, when the outermost one
 * settles.
 */
export async function runWithBufferedDatabaseChanges<T>(run: () => Promise<T>): Promise<T> {
  bufferState.openTransactionCount += 1;
  try {
    return await run();
  } finally {
    bufferState.openTransactionCount -= 1;
    if (bufferState.openTransactionCount === 0 && isNonEmpty(bufferState.bufferedDeliveries)) {
      const deliveries = bufferState.bufferedDeliveries;
      bufferState.bufferedDeliveries = [];
      for (const { event, listener } of deliveries) {
        invokeListener(listener, event);
      }
    }
  }
}
